import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { toggleVariants } from '@/components/ui/toggle';
import { Icons } from '@/components/icons';
import type { Editor } from '@tiptap/react';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ToolbarButton } from '../toolbar-button';
import { LinkEditBlock } from './link-edit-block';
import { useOnClickOutside } from 'usehooks-ts';
import type { RefObject } from 'react';

interface LinkEditPopoverProps extends VariantProps<typeof toggleVariants> {
  editor: Editor;
}

const LinkEditPopover = ({ editor, size, variant }: LinkEditPopoverProps) => {
  const [open, setOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const { from, to } = editor.state.selection;
  const text = editor.state.doc.textBetween(from, to, ' ');
  const { href } = editor.getAttributes('link');

  const onSetLink = React.useCallback(
    (url: string, text?: string) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .insertContent({
          type: 'text',
          text: text || url,
          marks: [
            {
              type: 'link',
              attrs: {
                href: url,
              },
            },
          ],
        })
        .setLink({ href: url })
        .run();

      setOpen(false);
    },
    [editor]
  );

  useOnClickOutside(popoverRef as RefObject<HTMLElement>, () => setOpen(false));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ToolbarButton
          isActive={editor.isActive('link')}
          tooltip={editor.isActive('link') ? 'Hantera länk' : 'Infoga länk'}
          aria-label={editor.isActive('link') ? 'Hantera länk' : 'Infoga länk'}
          disabled={editor.isActive('codeBlock')}
          size={size}
          variant={variant}
        >
          <Icons.link className='size-5' />
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent
        className='w-full min-w-80'
        align='end'
        side='bottom'
        onInteractOutside={() => setOpen(false)}
        ref={popoverRef}
      >
        <LinkEditBlock
          onSave={onSetLink}
          onCancel={() => setOpen(false)}
          defaultText={text}
          defaultUrl={href}
        />
      </PopoverContent>
    </Popover>
  );
};

export { LinkEditPopover };
